import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { useAuth } from '../context/AuthContext';

const AccountsDashboard = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentMethod: 'bank_transfer',
    transactionId: '',
    notes: ''
  });

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      const response = await axios.get('/leads', {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Handle different response formats
      let allLeads = [];
      if (Array.isArray(response.data)) {
        allLeads = response.data;
      } else if (response.data.leads && Array.isArray(response.data.leads)) {
        allLeads = response.data.leads;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        allLeads = response.data.data;
      }

      // Filter leads for accounts team
      const accountsLeads = allLeads.filter(lead =>
        lead.currentOwner === 'accounts'
      );

      setLeads(accountsLeads);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLoading(false);
    }
  };

  const recordPayment = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/leads/${selectedLead._id}/record-payment`,
        paymentData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowPaymentModal(false);
      setPaymentData({
        amount: '',
        paymentMethod: 'bank_transfer',
        transactionId: '',
        notes: ''
      });
      fetchLeads();
      alert('Payment recorded successfully');
    } catch (error) {
      console.error('Error recording payment:', error);
      alert('Error recording payment');
    }
  };

  const markCompleted = async (leadId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/leads/${leadId}/mark-completed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLeads();
      alert('Lead marked as completed');
    } catch (error) {
      console.error('Error marking completed:', error);
      alert('Error marking completed');
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Accounts Dashboard</h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm">Total Leads</h3>
            <p className="text-2xl font-bold">{leads.length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm">Payment Pending</h3>
            <p className="text-2xl font-bold">
              {leads.filter(l => l.workflowStatus === 'payment_pending').length}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm">Completed</h3>
            <p className="text-2xl font-bold">
              {leads.filter(l => l.workflowStatus === 'completed').length}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm">Total Revenue</h3>
            <p className="text-2xl font-bold">
              ₹{leads.reduce((sum, l) => sum + (l.quotations[0]?.amount || 0), 0).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destination</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {leads.map(lead => (
                <tr key={lead._id}>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium">{lead.customerName}</div>
                      <div className="text-sm text-gray-500">{lead.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">{lead.destination}</td>
                  <td className="px-6 py-4">
                    <span className="font-semibold">
                      ₹{lead.quotations[0]?.amount?.toLocaleString() || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${lead.workflowStatus === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                      }`}>
                      {lead.workflowStatus?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      {lead.workflowStatus === 'payment_pending' && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedLead(lead);
                              setShowPaymentModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            Record Payment
                          </button>
                          <button
                            onClick={() => markCompleted(lead._id)}
                            className="text-green-600 hover:text-green-800 text-sm"
                          >
                            Mark Complete
                          </button>
                        </>
                      )}
                      {lead.workflowStatus === 'completed' && (
                        <span className="text-green-600 text-sm">✓ Completed</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Payment Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-2xl font-bold mb-4">Record Payment</h2>
              <form onSubmit={recordPayment}>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Amount *</label>
                  <input
                    type="number"
                    required
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Payment Method *</label>
                  <select
                    value={paymentData.paymentMethod}
                    onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                    <option value="upi">UPI</option>
                    <option value="card">Card</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Transaction ID</label>
                  <input
                    type="text"
                    value={paymentData.transactionId}
                    onChange={(e) => setPaymentData({ ...paymentData, transactionId: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    rows="3"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowPaymentModal(false)}
                    className="px-4 py-2 border rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Record Payment
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountsDashboard;
